/*
 * dem.js — ground elevation under every point of a track, from Terrarium
 * terrain tiles (AWS Open Data / Mapzen, the same source and the same proxy
 * chain as /elevation).
 *
 *   GpsDem.sample(lat, lon, { urls:[template…], zoom, onProgress, signal }) → Promise<Float32Array>
 *
 * Why a track needs it: a phone's GPS altitude wanders by metres from one fix
 * to the next, and summing that noise is how a flat run «climbs» 300 m.
 * Measured against barometric watches on 11 real activities (gain, 5 m
 * hysteresis on a 30 m grid): terrain is within 22 % on average with no bias,
 * where GPS altitude is ~35 % off even after the best filtering.
 *
 * Height = R·256 + G + B/256 − 32768. The PNG is decoded with colour
 * management OFF — a browser that converts the tile to the display's colour
 * space changes the channel values, i.e. the heights.
 */
(function () {
    'use strict';
    var TILE = 256, RAD = Math.PI / 180;

    function tileXY(lat, lon, z) {
        var n = Math.pow(2, z);
        var x = (lon + 180) / 360 * n * TILE;
        var s = Math.sin(Math.max(-85.05, Math.min(85.05, lat)) * RAD);
        var y = (0.5 - Math.log((1 + s) / (1 - s)) / (4 * Math.PI)) * n * TILE;
        return [x, y];
    }

    function decode(blob) {
        if (typeof createImageBitmap === 'function') {
            return createImageBitmap(blob, { colorSpaceConversion: 'none', premultiplyAlpha: 'none' }).then(function (bmp) {
                var c = typeof OffscreenCanvas !== 'undefined' ? new OffscreenCanvas(TILE, TILE) : document.createElement('canvas');
                c.width = TILE; c.height = TILE;
                var cx = c.getContext('2d', { willReadFrequently: true });
                cx.drawImage(bmp, 0, 0);
                if (bmp.close) bmp.close();
                return cx.getImageData(0, 0, TILE, TILE).data;
            });
        }
        return new Promise(function (res, rej) {
            var img = new Image();
            var u = URL.createObjectURL(blob);
            img.onload = function () {
                var c = document.createElement('canvas'); c.width = TILE; c.height = TILE;
                var cx = c.getContext('2d', { willReadFrequently: true });
                cx.drawImage(img, 0, 0);
                URL.revokeObjectURL(u);
                res(cx.getImageData(0, 0, TILE, TILE).data);
            };
            img.onerror = function () { URL.revokeObjectURL(u); rej(new Error('tile-decode')); };
            img.src = u;
        });
    }

    function fetchTile(urls, z, x, y, signal) {
        var i = 0;
        function attempt() {
            if (i >= urls.length) return Promise.reject(new Error('tile-unreachable'));
            var u = urls[i++].replace('{z}', z).replace('{x}', x).replace('{y}', y);
            return fetch(u, { signal: signal, mode: 'cors', credentials: 'omit' }).then(function (r) {
                if (!r.ok) throw new Error('http-' + r.status);
                return r.blob();
            }).then(decode).catch(function (e) {
                if (signal && signal.aborted) throw e;
                return attempt();
            });
        }
        return attempt();
    }

    function sample(lat, lon, o) {
        o = o || {};
        var n = lat.length;
        var z = o.zoom || 13;
        // keep the number of tiles sane for a long ride: drop a zoom level while > 160 tiles
        var need;
        for (;;) {
            need = {};
            var cnt = 0;
            for (var i = 0; i < n; i += Math.max(1, Math.floor(n / 20000))) {
                var p = tileXY(lat[i], lon[i], z);
                var k = Math.floor(p[0] / TILE) + '/' + Math.floor(p[1] / TILE);
                if (!need[k]) { need[k] = 1; cnt++; }
            }
            if (cnt <= 160 || z <= 9) break;
            z--;
        }
        var tiles = {}, keys = [], pos = new Float64Array(n * 2);
        for (var q = 0; q < n; q++) {
            var pp = tileXY(lat[q], lon[q], z);
            pos[q * 2] = pp[0] - 0.5; pos[q * 2 + 1] = pp[1] - 0.5;
            var x0 = Math.floor(pos[q * 2]), y0 = Math.floor(pos[q * 2 + 1]);
            for (var dx = 0; dx < 2; dx++) for (var dy = 0; dy < 2; dy++) {
                var kk = Math.floor((x0 + dx) / TILE) + '/' + Math.floor((y0 + dy) / TILE);
                if (!(kk in tiles)) { tiles[kk] = null; keys.push(kk); }
            }
        }
        var done = 0, total = keys.length, conc = 6, idx = 0, failed = 0;
        var max = Math.pow(2, z);
        return new Promise(function (resolve, reject) {
            function next() {
                if (o.signal && o.signal.aborted) { reject(new Error('aborted')); return; }
                if (idx >= keys.length) { if (done === total) finish(); return; }
                var key = keys[idx++], parts = key.split('/'), tx = ((+parts[0] % max) + max) % max, ty = +parts[1];
                if (ty < 0 || ty >= max) { tiles[key] = false; done++; next(); return; }
                fetchTile(o.urls, z, tx, ty, o.signal).then(function (d) { tiles[key] = d; }, function () { tiles[key] = false; failed++; })
                    .then(function () {
                        done++;
                        if (o.onProgress) o.onProgress(done / total);
                        if (done === total) finish(); else next();
                    });
            }
            function px(gx, gy) {
                var t = tiles[Math.floor(gx / TILE) + '/' + Math.floor(gy / TILE)];
                if (!t) return NaN;
                var ix = ((gx % TILE) + TILE) % TILE, iy = ((gy % TILE) + TILE) % TILE, off = (iy * TILE + ix) * 4;
                return t[off] * 256 + t[off + 1] + t[off + 2] / 256 - 32768;
            }
            function finish() {
                if (failed === total) { reject(new Error('dem-unreachable')); return; }
                var out = new Float32Array(n);
                for (var k = 0; k < n; k++) {
                    var fx = pos[k * 2], fy = pos[k * 2 + 1], ix = Math.floor(fx), iy = Math.floor(fy), ax = fx - ix, ay = fy - iy;
                    var a = px(ix, iy), b = px(ix + 1, iy), c = px(ix, iy + 1), d = px(ix + 1, iy + 1);
                    out[k] = a * (1 - ax) * (1 - ay) + b * ax * (1 - ay) + c * (1 - ax) * ay + d * ax * ay;
                }
                resolve({ ele: out, zoom: z, tiles: total, failed: failed });
            }
            if (!total) { finish(); return; }
            for (var w = 0; w < Math.min(conc, total); w++) next();
        });
    }

    window.GpsDem = { sample: sample, _tileXY: tileXY };
})();
